<template>
  <div class="card card-graph">
    <div class="card-header d-flex justify-content-between">
      <h5 class="card-title">{{ title }}</h5>
      <router-link
        v-if="historyLink"
        :to="{ name: 'analytics-dashboard-component', params: { deviceId: devId, componentname: 'license' } }"
        class="btn btn-primary btn-sm d-flex align-items-center gap-1"
      >
        <i class="bx bx-history"></i>
        History
      </router-link>
    </div>
    <div class="card-body">
      <div class="graph d-flex flex-wrap flex-col justify-content-center" ref="graphRef">
        <div class="d-flex flex-col justify-content-center" ref="charRef" style="height: 290px; width: 100%"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (min-width: 1024px) {
}

.graph {
  min-width: 200px;
  align-items: top;
}
</style>

<script>
import { onMounted, onUnmounted, onBeforeMount, onBeforeUnmount, ref, computed, watch } from 'vue'
import axios from 'axios'
import * as echarts from 'echarts'
import { useStore } from 'vuex'
import { applyEmptyChartState } from '@/data/chartEmptyState'

export default {
  name: 'GraphLicense',
  props: {
    host: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    refresh: {
      type: Number,
      default: 15,
    },
    title: {
      type: String,
      default: 'License Usage',
    },
    devId: {
      type: Number,
      default: 0,
    },
    historyLink: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const timer = ref(null)
    const chart = ref(null)
    const charRef = ref(null)
    const graphRef = ref(null)
    const store = useStore()

    const layoutMode = computed(() => store.state.layout.layoutMode)
    const apiKey = computed(() => store.state.auth.user?.accessToken)
    const apiHost = computed(() => window.apiHost)

    const strDurationStart = ref('-60m')
    const strDurationEnd = ref('0m')
    const strInterval = ref('5m')

    const resizeObserver = new ResizeObserver(() => onWindowResize())

    watch(layoutMode, (newMode) => {
      if (chart.value !== null) {
        const theme = newMode === 'dark' ? 'dark' : 'light'
        chart.value.dispose()
        chart.value = echarts.init(charRef.value, theme)
        drawChart()
      }
    })

    const graphUpdateCount = computed(() => store.state.graph.graphUpdateCount)
    watch(graphUpdateCount, () => {
      const _strDurationStart = computed(() => store.state.graph.graphDurationStart)
      const _strDurationEnd = computed(() => store.state.graph.graphDurationEnd)
      const _strInterval = computed(() => store.state.graph.graphInterval)

      strDurationEnd.value = _strDurationEnd.value
      strDurationStart.value = _strDurationStart.value
      strInterval.value = _strInterval.value
      
      if (chart.value !== null) {
        chart.value.dispose()
        chart.value = null
        initChart()
      }
      chart.value.showLoading({ 
        text: 'Loading data, please wait...', 
        color: '#4f6bed', 
        textColor: '#000', 
        maskColor: 'rgba(255, 255, 255, 0.2)', 
        zlevel: 0 
      })
      main()
    })

    function getChartOptions(data) {
      const times = []
      const values = []

      if (data && data.length > 0) {
        data.forEach(item => {
          times.push(item[0])
          values.push(item[1])
        })
      }

      const hasData = times.length > 0

      const options = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985'
            }
          },
          formatter: function (params) {
            if (params && params.length > 0) {
              const timestamp = params[0].data[0]
              const value = params[0].data[1]
              const date = new Date(timestamp)
              
              // Format date as: Jan 24, 2025
              const dateStr = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })
              
              // Format time as: 14:30:45
              const timeStr = date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: false
              })
              
              return `
                <div style="padding: 5px;">
                  <strong>${dateStr}</strong><br/>
                  <strong>${timeStr}</strong><br/>
                  <span style="color: #73bf69;">●</span> Devices: <strong>${value}</strong>
                </div>
              `
            }
            return ''
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#73bf69',
          borderWidth: 1,
          textStyle: {
            color: '#fff'
          }
        },
        xAxis: {
          type: 'time',
          boundaryGap: false,
          axisLabel: {
            formatter: function (value) {
              const date = new Date(value)
              return date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false
              })
            },
          },
        },
        yAxis: {
          type: 'value',
          name: 'Devices',
          axisLabel: {
            formatter: '{value}',
          },
        },
        series: [
          {
            name: 'License Count',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            data: times.map((time, index) => [time, values[index]]),
            areaStyle: hasData
              ? {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(115, 191, 105, 0.5)' },
                    { offset: 1, color: 'rgba(115, 191, 105, 0.1)' },
                  ]),
                }
              : undefined,
            lineStyle: {
              color: '#73bf69',
              width: 2,
            },
            itemStyle: {
              color: '#73bf69',
            },
            emphasis: {
              itemStyle: {
                color: '#73bf69',
                borderColor: '#fff',
                borderWidth: 2,
                shadowBlur: 10,
                shadowColor: 'rgba(115, 191, 105, 0.5)'
              }
            }
          },
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
      }

      return applyEmptyChartState(options, hasData, layoutMode.value === 'dark')
    }

    async function drawChart(data) {
      const option = getChartOptions(data)
      option.backgroundColor = layoutMode.value === 'dark' ? '#22354e' : '#ffffff'
      chart.value.setOption(option)
      chart.value.hideLoading()
    }

    function initChart() {
      if (chart.value === null) {
        const theme = layoutMode.value === 'dark' ? 'dark' : 'light'
        chart.value = echarts.init(charRef.value, theme)
      }
      const option = getChartOptions([])
      option.backgroundColor = layoutMode.value === 'dark' ? '#22354e' : '#ffffff'
      chart.value.setOption(option)
    }

    function createQuery(host, type, start, end, interval) {
      // Create Flux query for license data
      // Adjust this query based on your actual data structure
      return `
        from(bucket: "telegraf")
          |> range(start: ${start}, stop: ${end})
          |> filter(fn: (r) => r["_measurement"] == "ezelink_license_value")
          |> filter(fn: (r) => r["_field"] == "value")
          |> filter(fn: (r) => r["host"] == "${host}")
          |> filter(fn: (r) => r["instance"] == "connected" or r["instance"] == "utilization" )
          |> aggregateWindow(every: ${interval}, fn: max, createEmpty: false)
      `
    }

    async function main() {
      try {
        const query = createQuery(props.host, props.type, strDurationStart.value, strDurationEnd.value, strInterval.value)
        
        const response = await axios.post(apiHost.value + '/core/api/v1/analytics/query', query, {
          headers: {
            Authorization: 'Bearer ' + apiKey.value,
            'Content-Type': 'application/vnd.flux',
            Accept: 'application/csv',
          },
        })

        const data = response.data
        const licenseData = []
        let fieldTime = 3
        let fieldValue = 5

        data.split('\n').forEach((line) => {
          const fields = line.trim().split(',')
          if (fields.length >= 5 && fields[1] === 'result') {
            fieldTime = fields.indexOf('_time')
            fieldValue = fields.indexOf('_value')
          } else if (fields.length >= 5 && fields[fieldTime] && fields[fieldValue]) {
            const val = parseFloat(fields[fieldValue])
            if (!isNaN(val)) {
              licenseData.push([fields[fieldTime], val])
            }
          }
        })

        drawChart(licenseData)
      } catch (error) {
        console.error('Error fetching license data:', error)
        // Draw empty chart on error
        drawChart([])
      }
    }

    function onWindowResize() {
      if (chart.value !== null) chart.value.resize()
    }

    onMounted(() => {
      initChart()
      main()
      if (props.refresh > 0) {
        timer.value = setInterval(() => main(), props.refresh * 1000)
      }
      if (graphRef.value) {
        resizeObserver.observe(graphRef.value)
      }
    })

    onUnmounted(() => {
      if (props.refresh > 0 && timer.value !== null) clearInterval(timer.value)
      if (chart.value !== null) {
        chart.value.dispose()
        chart.value = null
      }
    })

    onBeforeMount(() => {
      window.addEventListener('resize', onWindowResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onWindowResize)
      if (graphRef.value) {
        resizeObserver.unobserve(graphRef.value)
      }
    })

    return {
      charRef,
      graphRef,
    }
  },
}
</script>